import { useState, useCallback, useEffect } from 'react';
import './App.css';
import { Toaster, toast } from 'sonner';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { PasswordInput } from './components/ui/password-input';
import { Label } from './components/ui/label';
import { RubricBuilder } from './components/RubricBuilder';
import { TrackingSheetGenerator } from './components/TrackingSheetGenerator';
import { TemplateLibrary } from './components/TemplateLibrary';
import { TemplateSelectDialog } from './components/TemplateSelectDialog';
import { useRubrics } from './hooks/useRubrics';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { StudentsPage } from './pages/StudentsPage';
import { StudentDetailPage } from './pages/StudentDetailPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { FileText, LayoutGrid, Menu, X, Users, LogOut, Loader2, MessageSquare, CreditCard, Key, Share2, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './components/ui/dropdown-menu';

const APP_URL = process.env.REACT_APP_BACKEND_URL || 'https://rubric-builder-1.preview.emergentagent.com';

// Main authenticated app content
function AuthenticatedApp() {
  const { user, logout, isAdmin, changePassword } = useAuth();
  const { rubrics, loading, createRubric, updateRubric, deleteRubric, duplicateRubric, fetchRubrics } = useRubrics();
  
  // Navigation state
  const [currentView, setCurrentView] = useState('students'); // 'students', 'student-detail', 'rubric-builder', 'template-library', 'feedback', 'billing', 'admin-dashboard'
  const [activeTab, setActiveTab] = useState('build');
  
  // Change password dialog state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);
  
  // Selected entities
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [editingRubric, setEditingRubric] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  // Templates are rubrics without student_id
  const templates = rubrics.filter(r => !r.student_id);

  const handleSaveRubric = useCallback(async (rubricData) => {
    try {
      // Add student_id if we're creating for a student
      const dataWithStudent = {
        ...rubricData,
        student_id: selectedStudent?.id || null
      };

      if (editingRubric?.id) {
        const updated = await updateRubric(editingRubric.id, rubricData);
        setSelectedRubric(updated);
        setEditingRubric(null);
        toast.success('Rubric updated successfully');
      } else {
        const created = await createRubric(dataWithStudent);
        setSelectedRubric(created);
        toast.success('Rubric saved successfully');
      }
      setIsCreating(false);
      setActiveTab('generate');
      
      // Go back to student detail if we were creating for a student
      if (selectedStudent) {
        setCurrentView('student-detail');
      }
    } catch (error) {
      toast.error('Failed to save rubric');
    }
  }, [editingRubric, selectedStudent, createRubric, updateRubric]);

  // Navigation handlers
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setCurrentView('student-detail');
  };

  const handleBackToStudents = () => {
    setSelectedStudent(null);
    setCurrentView('students');
  };

  const handleCreateRubricForStudent = (student) => {
    setSelectedStudent(student);
    setSelectedRubric(null);
    setEditingRubric(null);
    setIsCreating(true);
    setCurrentView('rubric-builder');
    setActiveTab('build');
  };

  const handleSelectRubricFromStudent = (rubric) => {
    setSelectedRubric(rubric);
    setEditingRubric(rubric);
    setIsCreating(false);
    setCurrentView('rubric-builder');
    setActiveTab('build');
  };

  const handleAddFromTemplate = (student) => {
    setSelectedStudent(student);
    setTemplateDialogOpen(true);
  };

  const handleTemplateCloned = (clonedRubric) => {
    setSelectedRubric(clonedRubric);
    fetchRubrics(); // Refresh rubrics list
  };

  // Template library handlers (for the sidebar)
  const handleSelectRubric = (rubric) => {
    setSelectedRubric(rubric);
    setEditingRubric(rubric);
    setIsCreating(false);
    setCurrentView('rubric-builder');
    setActiveTab('build');
  };

  const handleEditRubric = (rubric) => {
    setSelectedRubric(rubric);
    setEditingRubric(rubric);
    setIsCreating(false);
    setCurrentView('rubric-builder');
    setActiveTab('build');
  };

  const handleDeleteRubric = async (rubricId) => {
    try {
      await deleteRubric(rubricId);
      if (selectedRubric?.id === rubricId) {
        setSelectedRubric(null);
      }
      if (editingRubric?.id === rubricId) {
        setEditingRubric(null);
        setIsCreating(false);
      }
      toast.success('Rubric deleted');
    } catch (error) {
      toast.error('Failed to delete rubric');
    }
  };

  const handleDuplicateRubric = async (rubricId) => {
    try {
      const duplicated = await duplicateRubric(rubricId);
      setSelectedRubric(duplicated);
      setEditingRubric(duplicated);
      setCurrentView('rubric-builder');
      setActiveTab('build');
      toast.success('Rubric duplicated');
    } catch (error) {
      toast.error('Failed to duplicate rubric');
    }
  };

  const handleCreateNewTemplate = () => {
    setSelectedStudent(null); // No student = template
    setSelectedRubric(null);
    setEditingRubric(null);
    setIsCreating(true);
    setCurrentView('rubric-builder');
    setActiveTab('build');
  };

  const handleGenerateSheet = (rubric) => {
    setSelectedRubric(rubric);
    setEditingRubric(null);
    setIsCreating(false);
    setCurrentView('rubric-builder');
    setActiveTab('generate');
  };

  const handleCancelEdit = () => {
    setEditingRubric(null);
    setIsCreating(false);
    if (selectedStudent) {
      setCurrentView('student-detail');
    }
  };

  const handleGoToTemplates = () => {
    setCurrentView('template-library');
    setSelectedStudent(null);
  };

  const handleGoToStudents = () => {
    setCurrentView('students');
    setSelectedStudent(null);
  };

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPwd(true);
    try {
      await changePassword(currentPwd, newPwd);
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setChangingPwd(false);
    }
  };

  const showBuilder = isCreating || editingRubric;

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <Toaster position="top-center" richColors />
      
      {/* Header - FrameEd Style */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5] sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {currentView === 'rubric-builder' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden rounded-xl"
                data-testid="toggle-sidebar-btn"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            )}
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-semibold text-lg text-[#1C1C1C]">Rubric Builder</h1>
                <p className="text-[10px] text-[#595959] -mt-0.5">A FrameEd Product</p>
              </div>
            </div>
          </div>
          
          {/* Navigation Tabs - FrameEd Style */}
          <div className="pill-tabs">
            <button
              data-testid="nav-students"
              className={`pill-tab ${currentView === 'students' || currentView === 'student-detail' ? 'pill-tab-active' : ''}`}
              onClick={handleGoToStudents}
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Students</span>
              </span>
            </button>
            <button
              data-testid="nav-templates"
              className={`pill-tab ${currentView === 'template-library' || currentView === 'rubric-builder' ? 'pill-tab-active' : ''}`}
              onClick={handleGoToTemplates}
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Templates</span>
              </span>
            </button>
            <button
              data-testid="nav-feedback"
              className={`pill-tab ${currentView === 'feedback' ? 'pill-tab-active' : ''}`}
              onClick={() => setCurrentView('feedback')}
            >
              <span className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Feedback</span>
              </span>
            </button>
          </div>

          {/* Share + User Menu */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-xl gap-2"
              data-testid="share-app-btn"
              onClick={async () => {
                const shareText = `Check out Rubric Builder - a free tool for SLPs to create professional rubrics and tracking sheets!\n\n${APP_URL}`;
                try {
                  await navigator.clipboard.writeText(shareText);
                  toast.success('Share text copied to clipboard!');
                } catch (err) {
                  // Fallback for browsers without clipboard API
                  const textArea = document.createElement('textarea');
                  textArea.value = shareText;
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textArea);
                  toast.success('Share text copied to clipboard!');
                }
              }}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="hidden sm:inline text-sm">{user?.email?.split('@')[0]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {user?.email}
                {isAdmin && <span className="ml-2 text-xs text-primary">(Admin)</span>}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="menu-change-password-btn">
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCurrentView('feedback')} data-testid="menu-feedback-btn">
                <MessageSquare className="w-4 h-4 mr-2" />
                Feedback & Ideas
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCurrentView('billing')} data-testid="menu-billing-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                Billing
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setCurrentView('admin-dashboard')} data-testid="menu-admin-dashboard-btn">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    What's New (Admin)
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {currentView === 'students' && (
        <main className="p-4 sm:p-6 lg:p-8">
          <StudentsPage onSelectStudent={handleSelectStudent} />
        </main>
      )}

      {currentView === 'student-detail' && selectedStudent && (
        <main className="p-4 sm:p-6 lg:p-8">
          <StudentDetailPage
            student={selectedStudent}
            onBack={handleBackToStudents}
            onCreateRubric={handleCreateRubricForStudent}
            onSelectRubric={handleSelectRubricFromStudent}
            onAddFromTemplate={handleAddFromTemplate}
          />
        </main>
      )}

      {currentView === 'feedback' && (
        <main className="p-4 sm:p-6 lg:p-8">
          <FeedbackPage />
        </main>
      )}

      {currentView === 'billing' && (
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-border p-8 text-center">
              <CreditCard className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
              <h2 className="text-xl font-semibold mb-2">Billing</h2>
              <p className="text-muted-foreground mb-4">
                Billing features are coming soon.
              </p>
              <p className="text-sm text-muted-foreground">
                You're currently on the <span className="font-medium text-primary">Free Plan</span>.
              </p>
            </div>
          </div>
        </main>
      )}

      {currentView === 'admin-dashboard' && isAdmin && (
        <main className="p-4 sm:p-6 lg:p-8">
          <AdminDashboard />
        </main>
      )}

      {currentView === 'template-library' && (
        <div className="flex min-h-[calc(100vh-64px)]">
          {/* Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <aside className="w-80 h-[calc(100vh-64px)] bg-white border-r border-border sticky top-16 overflow-hidden no-print">
              <TemplateLibrary
                rubrics={templates}
                selectedRubric={selectedRubric}
                editingRubric={editingRubric}
                onSelect={handleSelectRubric}
                onEdit={handleEditRubric}
                onDelete={handleDeleteRubric}
                onDuplicate={handleDuplicateRubric}
                onCreateNew={handleCreateNewTemplate}
                onGenerateSheet={handleGenerateSheet}
                loading={loading}
              />
            </aside>
          </div>

          {/* Main Content */}
          <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pb-24">
            <div className="max-w-4xl mx-auto text-center py-16">
              <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium text-muted-foreground">
                Templates
              </h3>
              <p className="text-sm text-muted-foreground mt-1 mb-6">
                Select a template from the sidebar to edit, or create a new one.
              </p>
              <Button
                onClick={handleCreateNewTemplate}
                data-testid="create-template-btn"
                className="rounded-xl"
              >
                Create New Template
              </Button>
            </div>
          </main>
        </div>
      )}

      {currentView === 'rubric-builder' && (
        <div className="flex min-h-[calc(100vh-64px)]">
          {/* Sidebar - only show for templates, not student rubrics */}
          {!selectedStudent && (
            <>
              <div className="hidden lg:block w-80 flex-shrink-0">
                <aside className="w-80 h-[calc(100vh-64px)] bg-white border-r border-border sticky top-16 overflow-hidden no-print">
                  <TemplateLibrary
                    rubrics={templates}
                    selectedRubric={selectedRubric}
                    editingRubric={editingRubric}
                    onSelect={handleSelectRubric}
                    onEdit={handleEditRubric}
                    onDelete={handleDeleteRubric}
                    onDuplicate={handleDuplicateRubric}
                    onCreateNew={handleCreateNewTemplate}
                    onGenerateSheet={handleGenerateSheet}
                    loading={loading}
                  />
                </aside>
              </div>

              {/* Mobile Sidebar */}
              <aside 
                className={`${
                  sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } lg:hidden fixed top-16 left-0 z-40 w-80 h-[calc(100vh-64px)] bg-white border-r border-border transition-transform duration-200 no-print`}
              >
                <TemplateLibrary
                  rubrics={templates}
                  selectedRubric={selectedRubric}
                  editingRubric={editingRubric}
                  onSelect={handleSelectRubric}
                  onEdit={handleEditRubric}
                  onDelete={handleDeleteRubric}
                  onDuplicate={handleDuplicateRubric}
                  onCreateNew={handleCreateNewTemplate}
                  onGenerateSheet={handleGenerateSheet}
                  loading={loading}
                />
              </aside>

              {/* Mobile Overlay */}
              {sidebarOpen && (
                <div 
                  className="fixed inset-0 bg-black/20 z-30 lg:hidden no-print"
                  onClick={() => setSidebarOpen(false)}
                  style={{ top: '64px' }}
                />
              )}
            </>
          )}

          {/* Main Content Area */}
          <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pb-24">
            {/* Sub-tabs for Build/Generate when in builder */}
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="pill-tabs">
                  <button
                    data-testid="tab-build-rubric"
                    className={`pill-tab ${activeTab === 'build' ? 'pill-tab-active' : ''}`}
                    onClick={() => setActiveTab('build')}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" strokeWidth={1.5} />
                      Build
                    </span>
                  </button>
                  <button
                    data-testid="tab-generate-sheet"
                    className={`pill-tab ${activeTab === 'generate' ? 'pill-tab-active' : ''}`}
                    onClick={() => setActiveTab('generate')}
                    disabled={!selectedRubric}
                  >
                    <span className="flex items-center gap-2">
                      <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
                      Print
                    </span>
                  </button>
                </div>
              </div>

              {activeTab === 'build' && (
                <>
                  {showBuilder ? (
                    <div>
                      {selectedStudent && (
                        <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                          <p className="text-sm text-muted-foreground">
                            Creating rubric for: <span className="font-medium text-foreground">{selectedStudent.display_name}</span>
                          </p>
                        </div>
                      )}
                      {editingRubric && (
                        <div className="mb-4 flex items-center justify-between">
                          <h2 className="text-lg font-semibold">
                            Editing: {editingRubric.title}
                          </h2>
                        </div>
                      )}
                      <RubricBuilder
                        rubric={editingRubric}
                        onSave={handleSaveRubric}
                        onCancel={handleCancelEdit}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
                      <h3 className="text-lg font-medium text-muted-foreground">
                        Ready to Build
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-6">
                        Select a template from the sidebar to edit, or create a new one.
                      </p>
                      <Button
                        onClick={handleCreateNewTemplate}
                        data-testid="start-building-btn"
                        className="rounded-xl"
                      >
                        Create New Template
                      </Button>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'generate' && (
                <TrackingSheetGenerator rubric={selectedRubric} />
              )}
            </div>
          </main>
        </div>
      )}

      {/* Template Selection Dialog */}
      <TemplateSelectDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelectTemplate={handleTemplateCloned}
        studentId={selectedStudent?.id}
      />

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <PasswordInput
                id="currentPassword"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <PasswordInput
                id="newPassword"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowChangePassword(false);
                setCurrentPwd('');
                setNewPwd('');
                setConfirmPwd('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={changingPwd}
              data-testid="save-password-btn"
            >
              {changingPwd ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white border-t border-border py-4 px-6 no-print">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-sm text-muted-foreground">
          <p>Built for SLPs, by an SLP</p>
          <a 
            href="https://www.linkedin.com/in/pete-o-rourke-m-s-ccc-slp-61643b104"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-primary transition-colors"
            data-testid="linkedin-link"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            Follow Pete on LinkedIn
          </a>
        </div>
      </footer>
    </div>
  );
}

// Auth wrapper component
function AppContent() {
  const { isAuthenticated, loading, logout } = useAuth();
  const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'forgot', 'reset'
  const [resetToken, setResetToken] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);

  // Check for reset token in URL - this runs on mount BEFORE anything else
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setAuthMode('reset');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    setTokenChecked(true);
  }, []);

  // Wait for token check to complete
  if (!tokenChecked) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // IMPORTANT: Check for reset token BEFORE auth loading check
  // This allows users to reset password even when logged in
  if (authMode === 'reset' && resetToken) {
    return (
      <ResetPasswordPage 
        token={resetToken} 
        onComplete={() => {
          setResetToken(null);
          setAuthMode('login');
          // If user was logged in, log them out so they can log in with new password
          if (isAuthenticated) {
            logout();
          }
        }} 
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authMode === 'forgot') {
      return <ForgotPasswordPage onSwitchToLogin={() => setAuthMode('login')} />;
    }
    
    if (authMode === 'signup') {
      return <SignupPage onSwitchToLogin={() => setAuthMode('login')} />;
    }
    
    return (
      <LoginPage 
        onSwitchToSignup={() => setAuthMode('signup')}
        onSwitchToForgotPassword={() => setAuthMode('forgot')}
      />
    );
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
